/**
 * Confirm a planned instalment.
 *
 * Both the amount and the date are editable and prominent: the real instalment
 * on a variable-rate credit differs from the plan, and a payment made late
 * happened on the day it happened. Guessing silently would put a wrong number
 * in the ledger.
 *
 * The dialog states plainly whether saving will touch a balance. That is the
 * one thing the user must not have to infer.
 */
import { useEffect, useState } from 'react';
import { Loader2, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input, Textarea } from '@/components/ui/input';
import { Label } from '@/components/ui/misc';
import { ResponsiveDialog } from '@/components/ui/dialog';
import { AmountInput } from '@/components/AmountInput';
import { formatDate, formatMoney, todayIso, toCanonicalAmount } from '@/lib/format';
import { usePayInstalment } from '@/hooks/useApi';
import type { UpcomingInstalment } from '@/lib/types';

export function PayInstalmentDialog({
  instalment,
  onClose,
}: {
  instalment: UpcomingInstalment | null;
  onClose: () => void;
}): JSX.Element {
  const [amountRaw, setAmountRaw] = useState('');
  const [amountMinor, setAmountMinor] = useState<number | null>(null);
  const [date, setDate] = useState(todayIso());
  const [note, setNote] = useState('');
  const pay = usePayInstalment();

  useEffect(() => {
    if (!instalment) return;
    setAmountRaw(toCanonicalAmount(instalment.amountMinor, instalment.decimals));
    setAmountMinor(instalment.amountMinor);
    // Never default into the future: a future-dated entry would not show up in
    // today's balance, and "I paid it but nothing changed" is the worst
    // possible outcome of pressing this button.
    setDate(instalment.dueDate > todayIso() ? todayIso() : instalment.dueDate);
    setNote('');
  }, [instalment]);

  if (!instalment) {
    return <ResponsiveDialog open={false} onOpenChange={onClose} title="" children={null} />;
  }

  const differs = amountMinor != null && amountMinor !== instalment.amountMinor;
  const canSave = amountMinor != null && amountMinor > 0 && date <= todayIso();

  const handleSave = async () => {
    if (!canSave || amountMinor == null) return;
    try {
      const res = await pay.mutateAsync({
        id: instalment.id,
        amount: toCanonicalAmount(amountMinor, instalment.decimals),
        date,
        note: note || undefined,
      });
      toast.success(
        res.wroteToLedger
          ? 'Платено — операцията е записана и салдото е обновено'
          : 'Отбелязано като платено (извън касите — салдата не се променят)',
      );
      onClose();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <ResponsiveDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Потвърждаване на плащане"
      description={instalment.name}
      className="sm:max-w-md"
    >
      <div className="space-y-4">
        <div className="rounded-md border bg-muted/40 p-3 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Падеж</span>
            <span className={cn('tabular', instalment.overdue && 'text-destructive')}>
              {formatDate(instalment.dueDate)}
              {instalment.overdue ? ' · просрочена' : ''}
            </span>
          </div>
          <div className="mt-1 flex justify-between">
            <span className="text-muted-foreground">По план</span>
            <span className="tabular">
              {formatMoney(instalment.amountMinor, instalment.currency, instalment.decimals)}
            </span>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="pi-amount">Реално платена сума</Label>
          <AmountInput
            id="pi-amount"
            value={amountRaw}
            onChange={setAmountRaw}
            onValueChange={setAmountMinor}
            currency={instalment.currency}
            decimals={instalment.decimals}
            autoFocus
          />
          {differs ? (
            <p className="text-xs text-muted-foreground">
              Различава се от планираната — записва се реално платеното.
            </p>
          ) : null}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="pi-date">Дата на плащане</Label>
          <Input
            id="pi-date"
            type="date"
            value={date}
            max={todayIso()}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="pi-note">Бележка</Label>
          <Textarea id="pi-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>

        <p
          className={cn(
            'flex items-start gap-2 rounded-md px-3 py-2 text-xs',
            instalment.tracked ? 'bg-muted/60 text-muted-foreground' : 'border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400',
          )}
        >
          <Wallet className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {instalment.tracked
            ? 'Ще се запише реален разход и салдото ще намалее.'
            : 'Това задължение не е свързано с каса — ще се отбележи като платено, но салдата няма да се променят.'}
        </p>

        <div className="flex gap-2 pt-2">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Откажи
          </Button>
          <Button className="flex-1" disabled={!canSave || pay.isPending} onClick={() => void handleSave()}>
            {pay.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Потвърди
          </Button>
        </div>
      </div>
    </ResponsiveDialog>
  );
}
